/* eslint-disable max-lines */
import { BreakpointObserver } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Renderer2,
  ViewChild,
  ViewContainerRef,
  output,
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';

import { Store } from '@ngrx/store';
import { BehaviorSubject, EMPTY, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  skip,
  switchMap,
  takeUntil,
} from 'rxjs/operators';

import type { User } from '@core/account-types';
import { AnalyticsViewContext, provideAnalyticsViewContext } from '@core/analytics-legacy';
import type { Attachment } from '@core/attachments-types';
import {
  filterBoolean,
  formDetectChanges,
  LocalStorageService,
  trackByProp,
} from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { userToMention } from '@core/notes-data';
import type { NEW_NOTE_ID, Note, NoteId } from '@core/notes-types';
import type { TOfferKind } from '@core/offers-types';
import type {
  ConfigPlugins,
  EditorButton,
  MentionElementGroup,
  PluginsAPI,
} from '@core/prosemirror-legacy';
import {
  EmojiPluginPickerBeforeOpen,
  EmojiPluginPickerClose,
  LinkEditBeforeOpen,
  LinkEditClose,
  MentionsPluginPickerBeforeOpen,
  MentionsPluginPickerClose,
} from '@core/prosemirror-legacy';
import { MentionsStream$ } from '@core/prosemirror-legacy/angular';
import type { ProsemirrorDefaultConfigEvents } from '@core/prosemirror-legacy/webapp';
import {
  ProsemirrorDefaultConfig,
  ProsemirrorGroupedConfig,
  ProsemirrorGroupedConfigDirective,
  WysiwygComponent,
} from '@core/prosemirror-legacy/webapp';
import type { EditorJSONState } from '@core/prosemirror-types';
import {
  AttachmentItemComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  DialogService,
  DividerComponent,
  GroupComponent,
  NotificationsController,
  RtStatefulComponent,
  SpinnerComponent,
} from '@core/user-interface-legacy';
import { getHumanReadableFileSize, MaxFileSize, validateMaxFileSize } from '@core/utils-legacy';
import { VisibilitySelectorContainerComponent } from '@core/visibility-legacy';
import type { Visibility } from '@core/visibility-types';
import { DefaultVisibility, VisibilityLevel } from '@core/visibility-types';

import { NotesAnalytics } from '../notes.analytics';
import { FilteredMentions$ } from './filtered-mentions';

type SaveData = {
  candidateId: number;
  offerId: number;
  note: Partial<Note>;
  attachmentIds: number[];
};

type State = {
  active: boolean;
  keepFocus: boolean;
  value: EditorJSONState | null;
  savedValue: EditorJSONState | null;
  formTouched: boolean;
  attachmentsDirty: boolean;
  visibility: Visibility;
};

@Component({
  selector: 'rt-note-form',
  templateUrl: './note-form.component.html',
  styleUrls: ['./note-form.component.less'],
  host: {
    '[class.context-private]':
      'state.visibility && state.visibility?.level !== VisibilityLevel.Public',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideAnalyticsViewContext(AnalyticsViewContext.Notes), NotesAnalytics],
  providers: [FilteredMentions$, { provide: MentionsStream$, useExisting: FilteredMentions$ }],
  imports: [
    DividerComponent,
    SpinnerComponent,
    AttachmentItemComponent,
    GroupComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    ReactiveFormsModule,
    TranslatePipe,
    NgTemplateOutlet,
    VisibilitySelectorContainerComponent,
    WysiwygComponent,
    ProsemirrorGroupedConfigDirective,
  ],
})
export class NoteFormComponent extends RtStatefulComponent<State> {
  private readonly defaultVisibility: Visibility = DefaultVisibility;
  public readonly trackById = trackByProp<{ id: number }>({ prop: 'id' });

  @Input() public active: boolean = false;
  @Input() public offerId: number;
  @Input() public offerKind: TOfferKind;
  @Input() public candidateId: number;
  @Input() public candidateAdminsIds: number[];
  @Input() public canDeleteAttachments: boolean = false;
  @Input() public replyToId: number;
  @Input() public note: Note;
  @Input() public placeholder: string = '';
  @Input() public autofocus: boolean = false;
  @Input() public autosave: boolean = false;
  @Input() public attachmentsTemp: Array<{ uuid: string; attachment: Attachment }> = [];
  @Input() public attachmentLoading: boolean = false;
  @Input() public noteSaveLoading: boolean = false;
  @Input() public noteHasError: boolean = false;
  @Input() public allNotesFlat: Note[];
  @Input() public admins: User[] = [];
  @Input() public disallowedUsers: User[] = [];

  public changeNoteVisibility = output<boolean>();
  public typingStart = output<void>();
  public typingFinish = output<void>();
  public cancelClick = output<Note>();
  public noteSaveClick = output<SaveData>();
  public noteEditClick = output<{
    id: NoteId;
  }>();
  public replyChanges = output<{
    replyToId: number;
    value: object | null;
  }>();

  public uploadAttachmentClick = output<{
    attachment: {
      file: File;
    };
    note: {
      replyToId: number;
      id: number | null | typeof NEW_NOTE_ID;
    };
  }>();
  public deleteAttachmentClick = output<{
    attachmentId: number;
    note: Note;
  }>();

  @ViewChild('fileInput', { static: true }) public fileInput: ElementRef<HTMLInputElement>;

  public isSmallResolution: boolean = false;
  public wysiwygControl = new UntypedFormControl();
  public actionButtons = [
    <EditorButton>{
      icon: 'attach-file',
      tooltipText: 'app.candidate.add_attachment',
      isActive: () => false,
      run: () => this.fileInput.nativeElement.click(),
    },
  ];

  public VisibilityLevel: typeof VisibilityLevel = VisibilityLevel;
  public ProsemirrorDefaultConfig = ProsemirrorDefaultConfig;
  public ProsemirrorGroupedConfig = ProsemirrorGroupedConfig;

  private wysiwygComponent$ =
    new BehaviorSubject<WysiwygComponent<ProsemirrorDefaultConfig> | null>(null);
  private ngOnDestroy$ = new Subject<void>();
  private initialState: State = {
    active: false,
    keepFocus: false,
    value: null,
    savedValue: null,
    formTouched: false,
    attachmentsDirty: false,
    visibility: this.defaultVisibility,
  };

  constructor(
    private dialogService: DialogService,
    private localStorageService: LocalStorageService,
    private element: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
    protected cdRef: ChangeDetectorRef,
    private breakpointObserver: BreakpointObserver,
    private i18n: I18nService,
    private notificationsController: NotificationsController,
    private analytics: NotesAnalytics,
    private filteredMentions$: FilteredMentions$,
    private store: Store,
  ) {
    super(cdRef);

    this.createState(this.initialState);
  }

  @ViewChild(WysiwygComponent)
  private set wysiwygComponent(value: WysiwygComponent<ProsemirrorDefaultConfig>) {
    if (value === this.wysiwygComponent$.value) return;

    this.wysiwygComponent$.next(value);
  }

  public ngOnInit(): void {
    this.breakpointObserver
      .observe(['(max-width: 1439px)'])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(({ matches }) => {
        this.isSmallResolution = matches;
        this.cdRef.detectChanges();
      });

    if (this.note && this.note.bodyJson) {
      this.setState({
        ...this.state,
        active: true,
        visibility: this.note.visibility,
      });
    }

    if (this.autosave && !(this.note && this.note.bodyJson)) {
      this.restoreNoteContents();

      this.wysiwygControl.valueChanges
        .pipe(debounceTime(300), takeUntil(this.ngOnDestroy$))
        .subscribe(() => this.saveNoteContents());
    }

    this.state$
      .pipe(
        map(state => state.visibility),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(visibility => {
        const isPrivateVisibilityFlag = visibility?.level !== VisibilityLevel.Public;
        this.changeNoteVisibilityClass(isPrivateVisibilityFlag);
        this.changeNoteVisibility.emit(isPrivateVisibilityFlag);
      });

    this.wysiwygControl.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(value => this.onValueChange(value));

    this.wysiwygComponent$
      .pipe(
        filterBoolean,
        switchMap(wysiwyg =>
          wysiwyg.editorApi$.pipe(
            switchMap((api: PluginsAPI<ConfigPlugins<ProsemirrorDefaultConfig>>) =>
              api ? of({ wysiwyg, api }) : EMPTY,
            ),
          ),
        ),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(({ api, wysiwyg }) => {
        if (this.replyToId && wysiwyg) {
          const { mentionsService } = api;
          const parentNote = this.allNotesFlat.find(({ id }) => id === this.replyToId);
          if (parentNote && parentNote.admin) {
            const state = mentionsService.insertMentionAtSelection(userToMention(parentNote.admin));
            this.wysiwygControl.setValue(state.toJSON());
          }
        }
      });

    formDetectChanges(this.wysiwygControl, this.cdRef, this.ngOnDestroy$);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const hasError = changes.noteHasError ? changes.noteHasError.currentValue : this.noteHasError;

    if (changes.noteSaveLoading && !changes.noteSaveLoading.firstChange) {
      if (!changes.noteSaveLoading.currentValue && !hasError) {
        this.resetForm();
      }
    }

    if ('note' in changes) {
      this.setState(
        {
          ...this.state,
          attachmentsDirty: false,
        },
        false,
      );

      this.wysiwygControl.setValue(this.note.bodyJson);
    }

    if (
      ('admins' in changes && this.admins) ||
      ('disallowedUsers' in changes && this.disallowedUsers)
    ) {
      this.setUsersInMentions();
    }
  }

  public ngAfterContentInit(): void {
    const textarea = this.element.nativeElement.querySelector('textarea');

    if (this.autofocus && textarea) {
      textarea.focus();
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();

    if (this.active) {
      this.typingFinish.emit();
    }
  }

  public uploadAttachment(files: FileList | null, fileInput: HTMLInputElement): void {
    if (files && files.length > 0) {
      const file = files[0];
      const validSize = validateMaxFileSize(file, MaxFileSize.File50MB);

      if (!validSize) {
        const message = this.i18n.translate('app.common.errors.file_size_limit_exceeded', {
          limit: getHumanReadableFileSize(MaxFileSize.File50MB),
          fileSize: getHumanReadableFileSize(file.size),
        });
        this.notificationsController.error(message);

        return;
      }

      this.uploadAttachmentClick.emit({
        attachment: {
          file,
        },
        note: {
          replyToId: this.replyToId,

          id: (this.note && this.note.id) || null,
        },
      });

      this.setState(
        {
          ...this.state,
          attachmentsDirty: true,
        },
        false,
      );
    }
    // @ts-ignore
    fileInput.value = null;
  }

  public deleteAttachment(attachment: Attachment): void {
    this.deleteAttachmentClick.emit({
      note: this.note,
      attachmentId: attachment.id,
    });
  }

  public onValueChange(data: EditorJSONState): void {
    if (data !== this.state.value) {
      this.setState({ ...this.state, value: data });
      this.typingStart.emit();
    }
    if (this.replyToId) {
      this.replyChanges.emit({
        value: data,
        replyToId: this.replyToId,
      });
    }
  }

  public onFocusChange(focused: boolean): void {
    if (focused) {
      this.typingStart.emit();
      this.noteEditClick.emit({ id: this.note.id! });
    } else {
      if (
        this.wysiwygControl &&
        this.wysiwygControl.pristine &&
        !this.wysiwygControl.value &&
        !this.state.keepFocus &&
        !this.state.attachmentsDirty &&
        !this.hasAttachments()
      ) {
        this.cancel();
      }
    }
  }

  public onEditorEvents(event: ProsemirrorDefaultConfigEvents) {
    if (
      event instanceof EmojiPluginPickerBeforeOpen ||
      event instanceof MentionsPluginPickerBeforeOpen ||
      event instanceof LinkEditBeforeOpen
    ) {
      this.setKeepFocus();
    } else if (
      event instanceof EmojiPluginPickerClose ||
      event instanceof MentionsPluginPickerClose ||
      event instanceof LinkEditClose
    ) {
      this.setState({ ...this.state, keepFocus: false });
    }
  }

  public setKeepFocus(): void {
    this.setState({ ...this.state, keepFocus: true });
  }

  public isValid(): boolean {
    return !!this.wysiwygControl.value;
  }

  public hasVisibilityOptions(): boolean {
    if (this.replyToId) {
      return false;
    }

    return this.note && this.note.hasOwnProperty('replyToId') ? !this.note.replyToId : true;
  }

  public cancel(): void {
    this.resetForm();
    this.cancelClick.emit(this.note);
    this.typingFinish.emit();
  }

  public save(): void {
    if (!this.isValid()) {
      return;
    }

    this.typingFinish.emit();

    const note: Partial<Note> = {
      id: (this.note && this.note.id!) || null,
      replyToId: this.replyToId || (this.note && this.note.replyToId) || null,
      visibility: this.state.visibility,
      bodyJson: this.wysiwygControl.value,
    };

    const attachmentIds = this.getAttachmentIds();

    const payload = {
      candidateId: this.candidateId,
      offerId: this.offerId,
      note: note,
      attachmentIds,
    };
    this.replyChanges.emit({ value: null, replyToId: this.replyToId });
    this.noteSaveClick.emit(payload);
    this.analytics.trackNoteAdding(this.offerKind || 'candidate');
  }

  public changeNoteVisibilityClass(isPrivate: boolean): void {
    isPrivate
      ? this.renderer.addClass(this.element.nativeElement, 'context-private')
      : this.renderer.removeClass(this.element.nativeElement, 'context-private');
  }

  public trackByTempUpload<T extends { attachment: { id: number } }>(
    index: number,
    tempUpload: T,
  ): number {
    return tempUpload.attachment.id;
  }

  public onVisibilityChanges(visibility: Visibility): void {
    this.setState({
      ...this.state,
      visibility,
    });
  }

  public showMentionsInput(): boolean {
    return (!this.replyToId || !!(this.replyToId && this.state.value !== null)) && !!this.admins;
  }

  private hasAttachments(): boolean {
    let result = false;

    if (this.note) {
      result = this.note.attachments ? this.note.attachments.length > 0 : false;
    }

    if (!result && this.attachmentsTemp) {
      result = this.attachmentsTemp.length > 0;
    }

    return result;
  }

  private getAttachmentIds(): number[] {
    return [
      ...(this.attachmentsTemp ? this.attachmentsTemp : []).map(({ attachment }) => attachment.id),
      ...(this.note ? this.note.attachments || [] : []).map(attachment => attachment.id),
    ];
  }

  private noteContentsKey(): string {
    return `${this.candidateId}-${this.offerId}-${this.replyToId}`;
  }

  private restoreNoteContents(): void {
    const savedValue = this.localStorageService.get(this.noteContentsKey());

    if (savedValue) {
      this.noteEditClick.emit({ id: this.note.id! });
      this.setState({
        ...this.state,
        value: savedValue,
        savedValue,
      });
      this.wysiwygControl.setValue(savedValue);
    }
  }

  private saveNoteContents(): void {
    if (this.state.value && this.state.value !== this.state.savedValue) {
      const savedValue = this.wysiwygControl.value;
      this.localStorageService.set(this.noteContentsKey(), savedValue);
      this.setState({
        ...this.state,
        savedValue,
      });
    }
  }

  private clearNoteContents(): void {
    this.localStorageService.remove(this.noteContentsKey());
  }

  private resetForm(): void {
    this.setState(this.initialState);

    this.wysiwygControl.reset(null);
    if (this.autosave) {
      this.clearNoteContents();
    }
  }

  private setUsersInMentions() {
    const groupedMentions: MentionElementGroup[] = [
      { data: this.admins?.map(admin => userToMention(admin)) ?? [] },
      {
        header: this.i18n.translate('app.candidate.admin_select.no_access.header'),
        disabled: true,
        data:
          this.disallowedUsers?.map(user =>
            userToMention(user, {
              tooltip: this.i18n.translate(
                'app.candidate.admin_select.no_access.tooltip_share_on_the_go',
              ),
            }),
          ) ?? [],
      },
    ];
    this.filteredMentions$.next(groupedMentions);
  }
}
